class AutocompleteAPILayer {
  constructor () {
    this.eventBus = window.theme.autocompleteResults.eventBus

    this.apiKey = window.theme.collection.klevuAPIKey
    this.endpoint = window.theme.collection.klevuAPIURL
    this.market = `${window.Shopify.currency.active}_${window.Shopify.country}`

    this.offset = this.initializeCurrentPageFromSearchParams()
    this.searchTerm = this.initializeSearchTermFromSearchParams()
    this.activeSort = this.initializeActiveSortFromSearchParams()
    this.currentRequestId = 0 // Track request IDs to ignore stale responses

    // Handle filter initialization and reset logic
    this.initializeFilters()

    this.subscribeToEvents()
    this.getUUID()

    if (this.searchTerm !== null && this.searchTerm !== '') {
      this.fetchProducts(this.offset, this.activeSort, this.activeFacets)
    }
  }

  /**
   * Initialize filters - check for new search and reset if needed
   */
  initializeFilters () {
    const filterManager = window.theme.autocompleteResults.filters
    const url = new URL(window.location.href)
    const urlHasFilters = url.searchParams.get('search_filters')
    const previousSearchTerm = sessionStorage.getItem('currentSearchTerm')
    const isNewSearch =
      previousSearchTerm !== null && previousSearchTerm !== this.searchTerm

    let initialActiveFacets = []

    if (urlHasFilters) {
      // Preserve filters from URL (e.g., from bookmark or shared link)
      sessionStorage.setItem('currentSearchTerm', this.searchTerm)
      initialActiveFacets = filterManager.initializeFiltersFromUrl()
    } else if (isNewSearch) {
      // New search - clear filters
      sessionStorage.setItem('currentSearchTerm', this.searchTerm)
      filterManager.clearFiltersFromUrl()
    } else {
      // First load or same search - preserve filters from URL (if any)
      sessionStorage.setItem('currentSearchTerm', this.searchTerm)
      initialActiveFacets = filterManager.initializeFiltersFromUrl()
    }
    window.theme.autocompleteResults.stateLayer.state.activeFacets = initialActiveFacets

    // Always publish event to ensure UI is in sync, even when clearing filters
    this.eventBus.publish('state:activeFacets:updated', initialActiveFacets)
  }

  /**
   * Subscribes to search-related events and triggers product fetches.
   */
  subscribeToEvents () {
    this.eventBus.subscribe('state:search:term', term => {
      this.searchTerm = term
      this.offset = 0
      this.currentPage = 1
      // Increment request ID to invalidate any pending requests
      this.currentRequestId++
      this.fetchProducts(this.offset, this.activeSort, this.activeFacets)
    })

    this.eventBus.subscribe('state:offset:updated', offset => {
      this.offset = offset
      this.fetchProducts(offset, this.activeSort, this.activeFacets)
    })

    this.eventBus.subscribe('state:sort:updated', sort => {
      this.offset =
        window.theme.autocompleteResults.stateLayer.state.pagination.offset
      this.fetchProducts(this.offset, sort, this.activeFacets)
    })

    this.eventBus.subscribe('state:activeFacets:updated', () => {
      this.offset =
        window.theme.autocompleteResults.stateLayer.state.pagination.offset
      this.fetchProducts(this.offset, this.activeSort, this.activeFacets)
    })
  }

  /**
   * Fetches and stores Klevu UUID and IP address in sessionStorage.
   */
  async getUUID () {
    const url = `https://stats.ksearchnet.com/analytics/uuid`

    const options = {
      method: 'POST',
      url,
      headers: {
        'Content-Type': 'application/json',
      },
    }

    const response = await fetch(url, options)

    const data = await response.json()

    sessionStorage.setItem('klevu_uuid', data.klevu_uuid)
    sessionStorage.setItem('klevu_ip_address', data.ip_address)
  }

  /**
   * Initializes the current page and offset from URL search parameters.
   * @returns {number} The calculated offset
   */
  initializeCurrentPageFromSearchParams () {
    const url = new URL(window.location.href)
    const page = url.searchParams.get('auto_page')

    if (page) {
      this.currentPage = parseInt(page)
    } else {
      this.currentPage = 1
    }

    return (this.currentPage - 1) * this.limit
  }

  /**
   * Initializes the search term from URL and updates the header search input.
   * Handles filter preservation by skipping input event dispatch when filters exist.
   * @returns {string|null} The search term from URL
   */
  initializeSearchTermFromSearchParams () {
    const url = new URL(window.location.href)
    const searchTerm = url.searchParams.get('q')
    const hasFilters = url.searchParams.get('search_filters')
    const hasAutoPage = url.searchParams.get('auto_page')

    if (searchTerm) {
      const headerSearch = document.querySelector('header-search')

      if (headerSearch) {
        headerSearch.els.input.element.value = searchTerm

        // Only dispatch input event if there are NO filters
        // The input event triggers a re-fetch which loses the filters
        if (!hasFilters && !hasAutoPage) {
          headerSearch.els.input.element.dispatchEvent(
            new Event('input', { bubbles: true }),
          )
        } else {
          // Manually show results panel since we skipped the input event
          headerSearch.bodyToggle(false)
        }
        headerSearch.handleToggle()
        headerSearch.handlePointerUp()
      }
    }

    return searchTerm
  }

  /**
   * Initializes the active sort option from URL search parameters.
   * @returns {string} The sort option (defaults to 'RELEVANCE')
   */
  initializeActiveSortFromSearchParams () {
    const url = new URL(window.location.href)
    const sort = url.searchParams.get('sort_by')

    let activeSort = 'RELEVANCE'

    if (sort) {
      activeSort = sort
    }

    return activeSort
  }

  /**
   * Fetches products from the Klevu API.
   * Tracks request IDs to prevent stale responses from showing old results.
   * @param {number} offset - The pagination offset
   * @param {string} activeSort - The sort option
   * @param {Array} activeFacets - Array of active filter facets
   */
  async fetchProducts (offset, activeSort, activeFacets) {
    const url = `https://${this.endpoint}/cs/v2/search`
    // Capture the request ID and search term for this request
    const requestId = this.currentRequestId
    const requestSearchTerm = this.searchTerm

    const options = {
      method: 'POST',
      url,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        recordQueries: [
          {
            filters: {
              filtersToReturn: {
                enabled: true,
                options: {
                  order: 'INDEX',
                  limit: 750,
                },
                rangeFilterSettings: [
                  {
                    key: 'klevu_price',
                    minMax: 'true',
                  },
                ],
              },
              applyFilters: {
                filters: activeFacets,
              },
            },
            id: 'productSearch',
            typeOfRequest: 'SEARCH',
            settings: {
              sort: activeSort || 'RELEVANCE',
              limit: this.limit,
              offset,
              query: {
                term: requestSearchTerm,
              },
              fields: ['url', 'image', 'name', 'price', 'id', 'currency'],
              searchPrefs: ['hideOutOfStockProducts'],
              typeOfRecords: ['KLEVU_PRODUCT'],
              groupCondition: {
                groupOperator: 'ALL_OF',
                conditions: [
                  {
                    key: 'tags',
                    valueOperator: 'EXCLUDE',
                    singleSelect: false,
                    values: ['klevu-exclude-from-search'],
                  },
                ],
              },
            },
          },
          {
            id: 'categorySearch',
            typeOfRequest: 'SEARCH',
            settings: {
              query: {
                term: requestSearchTerm,
              },
              fields: ['url', 'name', 'id'],
              typeOfRecords: ['KLEVU_CATEGORY'],
              limit: 10,
            },
          },
        ],
        suggestions: [
          {
            id: 'suggestionsQuery',
            typeOfRequest: 'AUTO_SUGGESTIONS',
            query: requestSearchTerm,
            limit: this.suggestionsLimit,
            hlStartElem: '',
            hlEndElem: '',
          },
        ],
        context: {
          apiKeys: [this.apiKey],
        },
      }),
    }

    const response = await fetch(url, options)
    const data = await response.json()

    // Only process response if this is still the current request
    // This prevents stale responses from showing old results
    if (requestId !== this.currentRequestId) {
      return
    }

    // Also verify the search term hasn't changed
    if (this.searchTerm !== requestSearchTerm) {
      return
    }

    const filters = data.queryResults[0].filters
    const categoryResult = data.queryResults.find(
      r => r.id === 'categorySearch',
    )

    this.eventBus.publish('api:products:fetched', data)
    this.eventBus.publish('api:facets:fetched', filters)
    this.eventBus.publish('api:makers:fetched', categoryResult?.records || [])
  }

  get offset () {
    return window.theme.autocompleteResults.stateLayer.state.pagination.offset
  }

  set offset (offset) {
    window.theme.autocompleteResults.stateLayer.state.pagination.offset = offset
  }

  get activeSort () {
    return window.theme.autocompleteResults.stateLayer.state.activeSort
  }

  get activeFacets () {
    return window.theme.autocompleteResults.stateLayer.state.activeFacets
  }

  set activeFacets (activeFacets) {
    window.theme.autocompleteResults.stateLayer.state.activeFacets = activeFacets
  }

  set activeSort (activeSort) {
    window.theme.autocompleteResults.stateLayer.state.activeSort = activeSort
  }

  get currentPage () {
    return window.theme.autocompleteResults.stateLayer.state.pagination
      .currentPage
  }

  set currentPage (currentPage) {
    window.theme.autocompleteResults.stateLayer.state.pagination.currentPage = currentPage
  }

  get limit () {
    return Number(window.theme.collection.limit)
  }

  get suggestionsLimit () {
    return Number(window.theme.collection.quickLinksLimit)
  }
}

window.theme.autocompleteResults.apiLayer = new AutocompleteAPILayer()
